/**
 * ceci est la page container tableau flux des employées
 */
import React, { Fragment, useCallback, useMemo, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useFilterGroup } from "@hooks/useFilterGroup";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import { ArrowFlowDown, ArrowFlowUp } from "@routes/IconeSvg";
import { setEmployeProtocolMetadata } from "@store/actions/onboarding/_employeProtocolActions";

import { Avatar } from "@components/tailwindComponents";
import Button from "@components/tailwindComponents/Button";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import UseFilterImage from "@components/tailwindComponents/UseFilterImage";

import { BLANK_PROFILE_PICTURE } from "@helpers/_constants";
import { getDateToRender } from "@helpers/_functions";

import ModalRecruitementRequest from "../components/RecruitementRequestModal";
import animationData from "@assets/image/json/set_filters.json";
import DotLoader from "@components/tailwindComponents/DotLoader";

const BlackTooltip = withStyles(() => ({
  tooltip: {
    maxWidth: "200px",
    backgroundColor: "#3F3F51",
    fontFamily: "Poppins_Medium",
    fontSize: "13px",
    padding: "12px 17px"
  }
}))(Tooltip);

const lists = [
  { id: "post", name: "Poste" },
  { id: "location", name: "Emplacement" },
  { id: "date", name: "Date" },
  { id: "tasks", name: "Tâches effectuées" },
  { id: "group", name: "Groupe" },
  { id: "unavalaibility", name: "Type d’indisponibilité" },
  { id: "notes", name: "Notes" },
  { id: "typeOfDemission", name: "Raisons de départ" }
];

function TableEmployerFlow({ toggleLoadProtocol, filterValue }) {
  const [selected, setSelected] = useState(lists);
  const [showModalRequest, setShowModalRequest] = useState(false);
  const { t } = useTranslation("beginEnd.departure");
  const reduxDispatch = useDispatch();
  const { isGroupIdsMatchFilter } = useFilterGroup();

  const employerFlowData = useSelector(
    ({ onboarding }) => onboarding.employeFlow
  );
  const filteredEmployeFlowList = useMemo(() => {
    if (!filterValue) {
      return employerFlowData.list;
    }
    return employerFlowData.list.filter((employeFlow) => {
      if (
        filterValue.selectedFlux &&
        employeFlow.flow.type !== filterValue.selectedFlux
      ) {
        return false;
      }

      const employerEmplacements = employeFlow.employer.employerEmplacements;
      if (
        filterValue.selectedEmplacement.length &&
        employerEmplacements.some(
          (emplacement) =>
            !filterValue.selectedEmplacement.includes(emplacement.emplacementId)
        )
      ) {
        return false;
      }

      if (
        filterValue.employeeSelected.length &&
        !filterValue.employeeSelected.includes(employeFlow.employer._id)
      ) {
        return false;
      }

      if (
        filterValue.selectedType.length &&
        !filterValue.selectedType.includes(employeFlow.type)
      ) {
        return false;
      }

      if (
        filterValue.selectedTypeOfDemission.length &&
        !filterValue.selectedTypeOfDemission.includes(employeFlow.typeOfDemission)
      ) {
        return false;
      }

      if (
        filterValue.selectedGroupes?.length > 0 &&
        !isGroupIdsMatchFilter(
          employeFlow.employer,
          filterValue.selectedGroupes
        )
      )
        return false;

      return true;
    });
  }, [filterValue, employerFlowData]);

  const comeCount = useMemo(() => {
    return filteredEmployeFlowList.filter(
      (employeFlow) => employeFlow.flow.type === "come"
    ).length;
  }, [filteredEmployeFlowList]);

  const departureCount = useMemo(() => {
    return filteredEmployeFlowList.filter(
      (employeFlow) => employeFlow.flow.type === "departure"
    ).length;
  }, [filteredEmployeFlowList]);

  const toggleRecruitmentRequest = useCallback(() => {
    setShowModalRequest((_showModalRequest) => !_showModalRequest);
  }, []);

  const selectedKey = selected.map((list) => list.id);

  return (
    <div className="arh-w-full ">
      {/* Head liste tableau */}
      <div className="arh-flex arh-w-full arh-items-center arh-justify-between">
        <div className=" arh-flex arh-items-center">
          <div className="arh-me-5 arh-flex arh-items-center arh-space-x-2.5 arh-rounded-lg arh-border arh-border-solid arh-border-borderCardGreen arh-bg-cardGreen arh-px-5 arh-py-2">
            {ArrowFlowDown()}
            <span className="arh-font-poppins arh-text-sm arh-font-normal arh-text-fontCardGreen">
              {t("Arrivées")}
            </span>
            <span className="arh-font-poppins arh-text-sm arh-font-semibold arh-text-fontCardGreen">
              {comeCount}
            </span>
          </div>

          <div className="arh-flex arh-items-center arh-space-x-2.5 arh-rounded-lg arh-border arh-border-solid arh-border-[#FF99B2] arh-bg-[#FDD9D9] arh-px-5 arh-py-2">
            {ArrowFlowUp()}
            <span className="arh-font-poppins arh-text-sm arh-font-normal arh-text-[#912B44]">
              {t("Départs")}
            </span>
            <span className="arh-font-poppins arh-text-sm arh-font-semibold arh-text-[#912B44]">
              {departureCount}
            </span>
          </div>
        </div>
        <div className="arh-flex arh-justify-between">
          {/* Demande de recrutement */}
          <div className="arh-mr-[2.125rem] arh-flex arh-w-full">
            <Button onClick={toggleRecruitmentRequest}>
              {t("Demander un recrutement")}
            </Button>
          </div>

          <ListboxSimple
            placeholder={t("Visibilité colonnes")}
            name="visibility"
            optionList={lists}
            selectedValue={selected}
            setSelectedValue={setSelected}
            multiple={true}
            customTranslator={t}
            optionValueAttibut="id"
            showReset
          />
        </div>
      </div>

      {/* Table Flux des employées */}
      {
        employerFlowData?.isLoading ? (
          <div className="arh-h-[40dvh]">
            <DotLoader size="small" />
          </div>
        ) : !filteredEmployeFlowList.length ? (
          <UseFilterImage animationData={animationData}>{t("Aucun mouvement de personnel")}</UseFilterImage>
        ) : (
          <div className="arh-mt-9 arh-w-full arh-overflow-auto arh-h-full arh-max-h-[calc(100vh-82px)]">
            <table className="arh-w-full arh-table-fixed arh-font-poppins">
              <thead className="arh-sticky arh-top-0 arh-bg-greyColor">
                <tr className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable">
                  <th className="arh-w-[11%] arh-bg-greyColor arh-px-18 arh-py-[10px] arh-text-xs arh-font-semibold arh-text-darkColor desktop-middle:arh-w-[10%]">
                    {t("Nom employé")}
                  </th>
                  {selectedKey.includes("post") && (
                    <th className="arh-w-[7%] arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor desktop-middle:arh-w-[8%]">
                      {t("Poste")}
                    </th>
                  )}
                  {selectedKey.includes("location") && (
                    <th className="arh-w-[9%] arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {t("Emplacement")}
                    </th>
                  )}
                  {selectedKey.includes("date") && (
                    <th className="arh-w-[12%] arh-whitespace-nowrap arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {t("Date")}
                    </th>
                  )}
                  {selectedKey.includes("group") && (
                    <th className="arh-w-[10%] arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {t("Groupe")}
                    </th>
                  )}
                  <th className="arh-w-[13%] arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor">
                    {t("Supérieur hiérachique")}
                  </th>
                  {selectedKey.includes("typeOfDemission") && (
                    <th className="arh-w-[10%] arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {t("Raisons de départ")}
                    </th>
                  )}
                  {selectedKey.includes("unavalaibility") && (
                    <th className="arh-w-[10%] arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {t("Type")}
                    </th>
                  )}
                  {selectedKey.includes("notes") && (
                    <th className="arh-w-[8%] arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor">
                      {t("Notes")}
                    </th>
                  )}
                  {selectedKey.includes("tasks") && (
                    <th className="arh-w-[8%] arh-px-4 arh-text-xs arh-font-semibold arh-text-darkColor desktop-middle:arh-w-[8%]">
                      {t("Taches")}
                    </th>
                  )}
                  <th className="arh-w-[8%] arh-px-4 arh-text-center arh-text-xs arh-font-semibold arh-text-darkColor desktop-middle:arh-w-[12%]">
                    {t("Protocole")}
                    
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Gerer */}
                {filteredEmployeFlowList.map((itemFlow) => {
                  const itemKey = itemFlow._id + itemFlow.flow.type;
                  return (
                    <tr
                      className="arh-border-x-0 arh-border-b arh-border-t-0 arh-border-solid arh-border-borderTable hover:arh-bg-greyColor"
                      key={itemKey}
                    >
                      <td className="arh-px-18 arh-py-2 arh-text-fontBodyTable arh-font-medium arh-text-darkColor">
                        <div className="arh-flex arh-items-center arh-justify-between arh-space-x-2 arh-truncate">
                          <div className="arh-truncate">
                            <Avatar
                            src={itemFlow.employer.image || BLANK_PROFILE_PICTURE}
                            name={itemFlow.employer.fullName}
                            IsOverflowHIdden={true}
                          /></div>
                          <span className="desktop-middle:arh-ml-2">
                            {itemFlow.flow.type === "come"
                              ? ArrowFlowDown()
                              : ArrowFlowUp()}
                          </span>
                        </div>
                      </td>
                      {selectedKey.includes("post") && (
                        <td className="arh-truncate arh-px-4 arh-py-2 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <BlackTooltip
                            placement="top"
                            title={`${itemFlow.employer.post}`}
                            id={itemKey + "post"}
                          >
                            <span className="arh-w-full arh-max-w-fit ">
                              {itemFlow.employer.post || "-"}
                            </span>
                          </BlackTooltip>
                        </td>
                      )}
                      {selectedKey.includes("location") && (
                        <td className="arh-truncate arh-px-4 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <BlackTooltip
                            placement="top"
                            title={`${itemFlow.employer.emplacements || "-"}`}
                            id={itemKey + "location"}
                          >
                            <span className="arh-w-full arh-max-w-fit">
                              {itemFlow.employer.emplacements || "-"}
                            </span>
                          </BlackTooltip>
                        </td>
                      )}
                      {selectedKey.includes("date") && (
                        <td className="arh-whitespace-nowrap arh-px-4 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          {moment(getDateToRender(itemFlow.flow.date)).format(
                            "LL"
                          )}{" "}
                        </td>
                      )}
                      {selectedKey.includes("group") && (
                        <td className="arh-truncate arh-px-4 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <BlackTooltip
                            placement="top"
                            title={`${itemFlow.employer.groupsName}`}
                            id={itemKey + "groupe"}
                          >
                            <span className="arh-w-full arh-max-w-fit">
                              {itemFlow.employer.groupsName || "-"}
                            </span>
                          </BlackTooltip>
                        </td>
                      )}
                      <td className="arh-truncate arh-px-4 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                        <BlackTooltip
                          placement="top"
                          title={`${itemFlow.employer.superior}`}
                          id={itemKey + "name"}
                        >
                          <span className="arh-w-u arh-max-w-fit">
                            {itemFlow.employer.superior || "-"}
                          </span>
                        </BlackTooltip>
                      </td>

                      {selectedKey.includes("typeOfDemission") && (
                        <td className="arh-truncate arh-px-4 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <BlackTooltip
                            placement="top"
                            title={
                              itemFlow.typeOfDemission ? (
                                <p
                                  className="arh-m-0 arh-w-full"
                                  style={{
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 5,
                                    fontSize: "12px"
                                  }}
                                >
                                  {itemFlow.typeOfDemission}
                                </p>
                              ) : (
                                ""
                              )
                            }
                            id={itemKey + "typeOfDemission"}
                          >
                            <span className="arh-w-full">
                              {itemFlow.typeOfDemission || "-"}
                            </span>
                          </BlackTooltip>
                        </td>
                      )}
                      {selectedKey.includes("unavalaibility") && (
                        <td className="arh-truncate arh-px-4 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <BlackTooltip
                            placement="top"
                            title={
                              itemFlow.type ? (
                                <p
                                  className="arh-m-0 arh-w-full"
                                  style={{
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 5,
                                    fontSize: "12px"
                                  }}
                                >
                                  {itemFlow.type}
                                </p>
                              ) : (
                                ""
                              )
                            }
                            id={itemKey + "unavalaibility"}
                          >
                            <span className="arh-w-full">
                              {itemFlow.type || "-"}
                            </span>
                          </BlackTooltip>
                        </td>
                      )}
                      {selectedKey.includes("notes") && (
                        <td className="arh-truncate arh-px-4 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          <BlackTooltip
                            placement="top"
                            title={
                              itemFlow.notes ? (
                                <p
                                  className="arh-m-0 arh-w-full"
                                  style={{
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 5,
                                    fontSize: "12px"
                                  }}
                                >
                                  {itemFlow.notes}
                                </p>
                              ) : (
                                ""
                              )
                            }
                            id={itemKey + "notes"}
                          >
                            <div
                              className="GreenThemeSelect arh-w-full arh-truncate arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor"
                              id={itemKey + "div"}
                            >
                              {" "}
                              {itemFlow?.notes || "-"}{" "}
                            </div>
                          </BlackTooltip>
                        </td>
                      )}
                      {selectedKey.includes("tasks") && (
                        <td className="arh-truncate arh-px-4 arh-font-poppins arh-text-xs arh-font-medium arh-text-darkGreyColor">
                          {itemFlow?.employeeProtocol?._id
                            ? `${itemFlow?.employeeProtocol?.completedTasks || 0
                            }/${itemFlow?.employeeProtocol?.allTasks || 0}`
                            : "-"}
                        </td>
                      )}
                      <td className="arh-px-4">
                        <Button
                          variant={
                            itemFlow?.employeeProtocol?._id
                              ? "green-outlined"
                              : "green"
                          }
                          onClick={() => {
                            reduxDispatch(
                              setEmployeProtocolMetadata({
                                employeeId: itemFlow?.employer?._id,
                                flowType: itemFlow.flow.type,
                                unavailability: itemFlow._id || null,
                                employeProtocolId: itemFlow?.employeeProtocol?._id
                              })
                            );
                            toggleLoadProtocol();
                          }}
                        >
                          {itemFlow?.employeeProtocol?._id
                            ? t("Gérer")
                            : t("Assigner")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {/* Modal Demande de Recrutement */}
      <ModalRecruitementRequest
        show={showModalRequest}
        fragment={Fragment}
        onClickClose={toggleRecruitmentRequest}
      />
      {/* End Modal Demande de Recrutement */}
    </div>
  );
}

export default TableEmployerFlow;
